import debounce from 'lodash/debounce';
import { useCallback, useMemo, useRef, useState } from 'react';

import { useLazySearchClientsForRecordQuery } from '@app/api/Clients';
import { normalizePhoneSearchTerm } from '@app/components/Phone/utils';
import type { RecordClientSearchResult } from '@app/components/RecordModal/components/RecordDetails/types';
import { FRONT_WARNING, WARNING } from '@app/constants';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';

const CLIENT_SEARCH_RESULTS_LIMIT = 50;
const CLIENT_SEARCH_MIN_LENGTH = 3;
const CLIENT_SEARCH_DEBOUNCE_MS = 400;

type ClientSearchStatus = 'idle' | 'loading' | 'success';

interface UseRecordClientSearchProps {
  recordActiveBranchId: number | null | undefined;
  rightToViewContacts: boolean;
}

export const useRecordClientSearch = ({
  recordActiveBranchId,
  rightToViewContacts,
}: UseRecordClientSearchProps) => {
  const { showNotification } = useNotification();
  const [searchClientsForRecord] = useLazySearchClientsForRecordQuery();
  const [clientSearchTerm, setClientSearchTerm] = useState('');
  const [clientSearchStatus, setClientSearchStatus] =
    useState<ClientSearchStatus>('idle');
  const [clientSearchResults, setClientSearchResults] = useState<
    RecordClientSearchResult[]
  >([]);
  const clientSearchRequestIdRef = useRef(0);

  const searchClientsForRecordHandler = useCallback(
    async (searchTerm: string, requestId: number) => {
      if (!rightToViewContacts) {
        setClientSearchStatus('idle');
        return;
      }

      if (!recordActiveBranchId) {
        setClientSearchStatus('idle');
        showNotification(
          'Непредвиденная ошибка при поиске клиента: отсутствует BranchId',
          FRONT_WARNING
        );
        return;
      }

      try {
        const response = await searchClientsForRecord({
          searchTerm,
          take: CLIENT_SEARCH_RESULTS_LIMIT,
          ActiveBranchId: recordActiveBranchId,
        }).unwrap();

        if (requestId !== clientSearchRequestIdRef.current) return;

        setClientSearchResults(response);
        setClientSearchStatus('success');
      } catch (error) {
        if (requestId !== clientSearchRequestIdRef.current) return;

        setClientSearchStatus('idle');
        showNotification(error, WARNING);
      }
    },
    [
      recordActiveBranchId,
      rightToViewContacts,
      searchClientsForRecord,
      showNotification,
    ]
  );

  const debouncedSearchClientsForRecord = useMemo(
    () =>
      debounce(
        (searchTerm: string, requestId: number) =>
          void searchClientsForRecordHandler(searchTerm, requestId),
        CLIENT_SEARCH_DEBOUNCE_MS
      ),
    [searchClientsForRecordHandler]
  );

  const onChangeClientSearchTerm = useCallback(
    (value: string) => {
      const searchTerm = normalizePhoneSearchTerm(value);
      const requestId = ++clientSearchRequestIdRef.current;

      setClientSearchTerm(value);
      setClientSearchResults([]);
      debouncedSearchClientsForRecord.cancel();

      if (searchTerm.length < CLIENT_SEARCH_MIN_LENGTH) {
        setClientSearchStatus('idle');
        return;
      }

      setClientSearchStatus('loading');
      debouncedSearchClientsForRecord(searchTerm, requestId);
    },
    [debouncedSearchClientsForRecord]
  );

  const resetClientSearch = useCallback(() => {
    clientSearchRequestIdRef.current += 1;
    debouncedSearchClientsForRecord.cancel();
    setClientSearchTerm('');
    setClientSearchStatus('idle');
    setClientSearchResults([]);
  }, [debouncedSearchClientsForRecord]);

  // The threshold is checked against the same normalized term the request
  // uses, so "+7 (9" shows the hint instead of an empty dropdown.
  const isClientSearchTooShort =
    normalizePhoneSearchTerm(clientSearchTerm).length <
    CLIENT_SEARCH_MIN_LENGTH;

  return {
    clientSearchTerm,
    setClientSearchTerm,
    clientSearchResults,
    clientSearchIsLoading: clientSearchStatus === 'loading',
    clientSearchIsSuccess: clientSearchStatus === 'success',
    isClientSearchTooShort,
    onChangeClientSearchTerm,
    resetClientSearch,
  };
};
